/**
 * Copyright IT People Corporation. All Rights Reserved.
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Contributors: Mohan Venkataraman, Dilip Manjunatha, Aashish Shrestha, Sahinul Haque
 */

import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import config from '../utils/EnvironmentSwitcher';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CSpinner,
  CBadge,
  CButton,
  CInputGroup,
  CFormInput,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilWallet, cilMoney, cilHistory } from '@coreui/icons';
import 'react-toastify/dist/ReactToastify.css';
import '../scss/main.scss';


const BlockchainExplorer = ({ authUser, fhirToken }) => {
  const [walletInfo, setWalletInfo] = useState(null);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [itemId, setItemId] = useState('');
  const [itemHistory, setItemHistory] = useState([]);
  const [nftId, setNftId] = useState('');
  const [listingId, setListingId] = useState('');
  const [nftHistory, setNftHistory] = useState(null);
  const [listingHistory, setListingHistory] = useState([]);
  const [errors, setErrors] = useState({});
  const [dataStatus, setDataStatus] = useState({
    wallet: 'idle',    // idle, loading, success, error
    balance: 'idle',
    transactions: 'idle',
    itemHistory: 'idle',
    nftHistory: 'idle',
    listingHistory: 'idle'
  });
  //console.log(authUser);
  // Fetch user's wallet info
  const getAuthHeaders = async () => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      const firebaseToken = await currentUser.getIdToken();
      
      return {
        'Authorization': `Bearer ${firebaseToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw new Error('No authentication token available');
    }
  };

  // Add new state for API responses
  const [apiResponses, setApiResponses] = useState({
    wallet: null,
    balance: null,
    transactions: null
  });

  // Modified handleApiResponse to store raw responses
  const handleApiResponse = async (response, errorMessage) => {
    const contentType = response.headers.get("content-type");
    const responseText = await response.text();
    
    // Store the raw response
    const responseData = {
      status: response.status,
      contentType,
      data: responseText,
      timestamp: new Date().toISOString()
    };

    try {
      const parsedData = responseText ? JSON.parse(responseText) : null;
      if (!response.ok) {
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }
      return parsedData;
    } catch (error) {
      console.error('API Response Error:', error);
      throw error;
    }
  };

  // Fetch user's wallet info
  const fetchWalletInfo = async () => {
    setDataStatus(prev => ({ ...prev, wallet: 'loading' }));
    try {
      const actualAuthUser = authUser?.authUser || authUser;
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid || actualAuthUser?.uid;
      
      if (!userId) {
        throw new Error('No user ID available');
      }
      
      const headers = await getAuthHeaders();
      const response = await fetch(`${config.apiUrl}/api/wallet/${userId}`, {
        headers
      });
      
      setApiResponses(prev => ({
        ...prev,
        wallet: {
          url: `/api/wallet/${userId}`,
          status: response.status,
          timestamp: new Date().toISOString()
        }
      }));

      if (response.status === 404) {
        // Wallet doesn't exist yet - this is okay, user may need to create one
        setErrors(prev => ({ ...prev, wallet: 'Wallet not found. You may need to create a wallet first.' }));
        setDataStatus(prev => ({ ...prev, wallet: 'error' }));
        return;
      }
      
      const data = await handleApiResponse(response, 'Failed to fetch wallet');
      console.log('Wallet data received:', data);
      
      if (data?.walletAddress) {
        setWalletInfo({
          address: data.walletAddress
        });
        setDataStatus(prev => ({ ...prev, wallet: 'success' }));
      } else {
        setErrors(prev => ({ ...prev, wallet: 'No wallet address found in response' }));
        setDataStatus(prev => ({ ...prev, wallet: 'error' }));
      }
    } catch (error) {
      console.error('Wallet fetch error:', error);
      const errorMessage = error.message.includes('404') 
        ? 'Wallet not found. You may need to create a wallet first.'
        : error.message;
      setErrors(prev => ({ ...prev, wallet: errorMessage }));
      setDataStatus(prev => ({ ...prev, wallet: 'error' }));
    }
  };

  /**
   * Fetch user's balance. Auto-retries with ledger onboarding when user not on chain.
   * @param {boolean} isRetry - Internal: true when retrying after registerUser
   */
  const fetchBalance = async (isRetry = false) => {
    setDataStatus(prev => ({ ...prev, balance: 'loading' }));
    setErrors(prev => ({ ...prev, balance: null }));
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid || authUser?.uid || authUser?.authUser?.uid;
      
      if (!userId) {
        throw new Error('No user ID available');
      }
      
      const headers = await getAuthHeaders();
      const response = await fetch(`${config.apiUrl}/api/balance/${userId}`, {
        headers
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const msg = (data && (data.message || data.error)) || 'Unknown error';
        // Identity not found: try explicit registerUser and retry once
        const isLedgerRelated = /identity not found|balance not found|not found in the wallet|not on ledger/i.test(msg);
        if (isLedgerRelated && !isRetry) {
          try {
            const regRes = await fetch(`${config.apiUrl}/api/registerUser`, {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/json' },
              body: JSON.stringify({}),
            });
            const regData = await regRes.json().catch(() => ({}));
            if (regRes.ok || (regData?.message?.includes?.('already registered'))) {
              return fetchBalance(true); // Retry
            }
          } catch (regErr) {
            console.warn('Auto-register on balance failure:', regErr);
          }
        }
        setErrors(prev => ({ ...prev, balance: msg }));
        setDataStatus(prev => ({ ...prev, balance: 'error' }));
        return;
      }
      
      const data = await response.json();
      console.log('Balance data received:', data);
      
      if (data?.balance !== undefined) {
        setBalance({
          amount: data.balance,
          currency: 'MIE'
        });
        setDataStatus(prev => ({ ...prev, balance: 'success' }));
      } else {
        setErrors(prev => ({ ...prev, balance: 'No balance information available' }));
        setDataStatus(prev => ({ ...prev, balance: 'error' }));
      }
    } catch (error) {
      console.error('Balance fetch error:', error);
      const errorMessage = error.message.includes('404') 
        ? 'Balance not found. Wallet may not exist yet.'
        : error.message;
      setErrors(prev => ({ ...prev, balance: errorMessage }));
      setDataStatus(prev => ({ ...prev, balance: 'error' }));
    }
  };

  /** Manually trigger ledger onboarding when balance fails. */
  const handleRegisterWallet = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const userId = currentUser?.uid || authUser?.uid || authUser?.authUser?.uid;
    if (!userId) {
      toast.error('No user ID available');
      return;
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${config.apiUrl}/api/registerUser`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(data.message || 'Registered on ledger');
        await fetchBalance();
      } else {
        toast.error(data.message || data.error || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    setDataStatus(prev => ({ ...prev, transactions: 'loading' }));
    try {
      const actualAuthUser = authUser?.authUser || authUser;
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid || actualAuthUser?.uid;
      const isAdmin = actualAuthUser?.isAdmin || false;
      
      if (!userId) {
        throw new Error('No user ID available');
      }
      
      const endpoint = isAdmin 
        ? `${config.apiUrl}/api/transactions`
        : `${config.apiUrl}/api/transactions/${userId}`;
      
      console.log('Fetching transactions from:', endpoint);
      const headers = await getAuthHeaders();
      const response = await fetch(endpoint, {
        headers
      });
      
      if (response.status === 404) {
        // No transactions found - this is okay
        setTransactions([]);
        setDataStatus(prev => ({ ...prev, transactions: 'success' }));
        setErrors(prev => ({ ...prev, transactions: null }));
        return;
      }
      
      const data = await handleApiResponse(response, 'Failed to fetch transactions');
      console.log('Transactions data received:', data);
      
      // Normalize API transaction shape (field names vary across chaincode versions).
      const normalizeTransaction = (tx) => ({
            id: tx.id || tx.transactionId || tx.txId || tx.txID || 'unknown',
            timestamp: tx.timestamp || tx.createdAt || tx.date || Date.now(),
            type: tx.type || tx.transactionType || tx.docType || 'transfer',
            amount: tx.amount ?? tx.mierosAmount ?? tx.value ?? 0,
            status: tx.status || 'completed',
            raw: tx,
      });
      const transactionArray = (Array.isArray(data)
        ? data
        : (data?.transactions || data?.data || [])).map(normalizeTransaction);

      setTransactions(transactionArray);
      setDataStatus(prev => ({ ...prev, transactions: 'success' }));
      setErrors(prev => ({ ...prev, transactions: null }));
    } catch (error) {
      console.error('Transactions fetch error:', error);
      const errorMessage = error.message.includes('404') 
        ? 'No transactions found'
        : error.message;
      setErrors(prev => ({ ...prev, transactions: errorMessage }));
      setDataStatus(prev => ({ ...prev, transactions: 'error' }));
      toast.error(`Error fetching transactions: ${errorMessage}`);
    }
  };

  // Fetch item history
  const fetchItemHistory = async () => {
    if (!itemId) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${config.apiUrl}/api/item-history/${itemId}`, {
        headers
      });
      if (!response.ok) throw new Error('Failed to fetch item history');
      const data = await response.json();
      setItemHistory(data);
    } catch (error) {
      toast.error('Error fetching item history');
      console.error(error);
    }
  };

  // Fetch NFT Market History (listings and sales)
  const fetchNFTHistory = async () => {
    if (!nftId) {
      toast.error('Please enter an NFT ID');
      return;
    }
    
    setDataStatus(prev => ({ ...prev, nftHistory: 'loading' }));
    setErrors(prev => ({ ...prev, nftHistory: null }));
    
    try {
      // This is a public endpoint, no auth required
      const response = await fetch(`${config.apiUrl}/api/nft/${nftId}/history`);
      
      if (response.status === 404) {
        setNftHistory(null);
        setDataStatus(prev => ({ ...prev, nftHistory: 'success' }));
        setErrors(prev => ({ ...prev, nftHistory: 'NFT not found' }));
        toast.warning('NFT not found');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch NFT history (Status: ${response.status})`);
      }
      
      const data = await response.json();
      console.log('NFT History data received:', data);
      
      setNftHistory(data);
      setDataStatus(prev => ({ ...prev, nftHistory: 'success' }));
      setErrors(prev => ({ ...prev, nftHistory: null }));
      toast.success('NFT history loaded successfully');
    } catch (error) {
      console.error('NFT History fetch error:', error);
      setNftHistory(null);
      setDataStatus(prev => ({ ...prev, nftHistory: 'error' }));
      setErrors(prev => ({ ...prev, nftHistory: error.message }));
      toast.error(`Error fetching NFT history: ${error.message}`);
    }
  };

  // Fetch Listing History
  const fetchListingHistory = async () => {
    if (!listingId) {
      toast.error('Please enter a Listing ID');
      return;
    }
    
    setDataStatus(prev => ({ ...prev, listingHistory: 'loading' }));
    setErrors(prev => ({ ...prev, listingHistory: null }));
    
    try {
      // This is a public endpoint, no auth required
      const response = await fetch(`${config.apiUrl}/api/nft/listing/${listingId}/history`);
      
      if (response.status === 404) {
        setListingHistory([]);
        setDataStatus(prev => ({ ...prev, listingHistory: 'success' }));
        setErrors(prev => ({ ...prev, listingHistory: 'Listing not found' }));
        toast.warning('Listing not found');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch listing history (Status: ${response.status})`);
      }
      
      const data = await response.json();
      console.log('Listing History data received:', data);
      
      // Handle both array and object responses
      const historyArray = Array.isArray(data) ? data : (data?.history || []);
      setListingHistory(historyArray);
      setDataStatus(prev => ({ ...prev, listingHistory: 'success' }));
      setErrors(prev => ({ ...prev, listingHistory: null }));
      toast.success('Listing history loaded successfully');
    } catch (error) {
      console.error('Listing History fetch error:', error);
      setListingHistory([]);
      setDataStatus(prev => ({ ...prev, listingHistory: 'error' }));
      setErrors(prev => ({ ...prev, listingHistory: error.message }));
      toast.error(`Error fetching listing history: ${error.message}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Handle nested authUser structure from AuthUserContext
      // authUser can be: { authUser: { uid, email, ... } } or just { uid, email, ... }
      const actualAuthUser = authUser?.authUser || authUser;
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid || actualAuthUser?.uid;
      
      if (!userId) {
        console.log('Missing user ID, skipping data fetch', { 
          currentUser: !!currentUser,
          actualAuthUser,
          authUser 
        });
        setIsLoading(false);
        return;
      }
      
      console.log('Starting data fetch for user:', userId);
      setIsLoading(true);
      
      try {
        await Promise.all([
          fetchWalletInfo(),
          fetchBalance(),
          fetchTransactions()
        ]);
      } catch (error) {
        console.error('Error during data fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (authUser) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [authUser]);

  const getStatusBadge = (status) => {
    const colors = {
      'completed': 'success',
      'pending': 'warning',
      'failed': 'danger',
      'processing': 'info'
    };
    return <CBadge color={colors[status.toLowerCase()] || 'primary'}>{status}</CBadge>;
  };

  // Simplified render content function
  const renderContent = (type, content) => (
    <div>
      {dataStatus[type] === 'loading' && <CSpinner size="sm" className="me-2" />}
      {content}
    </div>
  );

  return (
    <div className="blockchain-explorer blockchain-explorer-visible">
      <CCard className="mb-4">
        <CCardHeader>
          <CIcon icon={cilWallet} className="me-2" />
          Wallet Information
        </CCardHeader>
        <CCardBody>
          {errors.wallet && dataStatus.wallet === 'error' && (
            <div className="alert alert-warning mb-3">
              <strong>Wallet:</strong> {errors.wallet}
            </div>
          )}
          {errors.balance && dataStatus.balance === 'error' && (
            <div className="alert alert-warning mb-3">
              <strong>Balance:</strong> {errors.balance}
            </div>
          )}
          <CRow>
            <CCol md={6}>
              <h5>Wallet Address</h5>
              <div>
                {dataStatus.wallet === 'loading' && <CSpinner size="sm" />}
                {dataStatus.wallet === 'success' && walletInfo && (
                  <p style={{ wordBreak: 'break-all' }}>{walletInfo.address}</p>
                )}
                {dataStatus.wallet === 'idle' && <p>Loading wallet info...</p>}
                {dataStatus.wallet === 'error' && !walletInfo && (
                  <p className="mb-2">Wallet information unavailable</p>
                )}
              </div>
            </CCol>
            <CCol md={6}>
              <h5>Balance</h5>
              <div>
                {dataStatus.balance === 'loading' && <CSpinner size="sm" />}
                {dataStatus.balance === 'success' && balance && (
                  <h3>{balance.amount} {balance.currency}</h3>
                )}
                {dataStatus.balance === 'idle' && <p>Loading balance...</p>}
                {dataStatus.balance === 'error' && !balance && (
                  <div>
                    <p className="mb-2">
                      {errors.balance || 'Balance information unavailable'}
                    </p>
                    <CButton color="primary" size="sm" onClick={handleRegisterWallet}>
                      Register on ledger
                    </CButton>
                  </div>
                )}
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
                {/* Transaction History Card */}
                <CCard className="mb-4">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <div>
                <CIcon icon={cilHistory} className="me-2" />
                Transaction History
              </div>
              <CButton
                color="primary"
                size="sm"
                onClick={fetchTransactions}
                disabled={dataStatus.transactions === 'loading'}
              >
                {dataStatus.transactions === 'loading' ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  'Refresh'
                )}
              </CButton>
            </CCardHeader>
            <CCardBody>
              {dataStatus.transactions === 'error' ? (
                <div className="alert alert-danger mb-3">
                  <strong>Error:</strong> {errors.transactions}
                </div>
              ) : null}
              {dataStatus.transactions === 'loading' && transactions.length === 0 ? (
                <div className="text-center py-3">
                  <CSpinner />
                  <p className="mt-2">Loading transactions...</p>
                </div>
              ) : transactions.length > 0 ? (
                <CTable hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Transaction ID</CTableHeaderCell>
                      <CTableHeaderCell>Date</CTableHeaderCell>
                      <CTableHeaderCell>Type</CTableHeaderCell>
                      <CTableHeaderCell>Amount</CTableHeaderCell>
                      <CTableHeaderCell>Status</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {transactions.map((tx) => (
                      <CTableRow key={tx.id}>
                        <CTableDataCell>{tx.id}</CTableDataCell>
                        <CTableDataCell>
                          {new Date(tx.timestamp).toLocaleString()}
                        </CTableDataCell>
                        <CTableDataCell>{tx.type}</CTableDataCell>
                        <CTableDataCell>{tx.amount}</CTableDataCell>
                        <CTableDataCell>
                          {getStatusBadge(tx.status)}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              ) : (
                <p className="mb-2">No transactions found</p>
              )}
            </CCardBody>
          </CCard>

          {/* NFT Market History Card */}
          <CCard className="mb-4">
            <CCardHeader>
              <CIcon icon={cilHistory} className="me-2" />
              NFT Market History
            </CCardHeader>
            <CCardBody>
              <CInputGroup className="mb-3">
                <CFormInput
                  type="text"
                  placeholder="Enter NFT ID to view market history"
                  value={nftId}
                  onChange={(e) => setNftId(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      fetchNFTHistory();
                    }
                  }}
                />
                <CButton
                  color="primary"
                  onClick={fetchNFTHistory}
                  disabled={dataStatus.nftHistory === 'loading' || !nftId}
                >
                  {dataStatus.nftHistory === 'loading' ? (
                    <>
                      <CSpinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    'Search'
                  )}
                </CButton>
              </CInputGroup>

              {errors.nftHistory && dataStatus.nftHistory === 'error' && (
                <div className="alert alert-danger mb-3">
                  <strong>Error:</strong> {errors.nftHistory}
                </div>
              )}

              {dataStatus.nftHistory === 'loading' && (
                <div className="text-center py-3">
                  <CSpinner />
                  <p className="mt-2">Loading NFT history...</p>
                </div>
              )}

              {nftHistory && dataStatus.nftHistory === 'success' && (
                <div>
                  <h5 className="mb-3">NFT ID: {nftHistory.nftId}</h5>
                  
                  {/* Listings Section */}
                  <div className="mb-4">
                    <h6>Listings ({nftHistory.listings?.length || 0})</h6>
                    {nftHistory.listings && nftHistory.listings.length > 0 ? (
                      <CTable hover responsive>
                        <CTableHead>
                          <CTableRow>
                            <CTableHeaderCell>Listing ID</CTableHeaderCell>
                            <CTableHeaderCell>Seller</CTableHeaderCell>
                            <CTableHeaderCell>Price</CTableHeaderCell>
                            <CTableHeaderCell>Status</CTableHeaderCell>
                            <CTableHeaderCell>Created At</CTableHeaderCell>
                          </CTableRow>
                        </CTableHead>
                        <CTableBody>
                          {nftHistory.listings.map((listing, index) => (
                            <CTableRow key={listing.listingID || index}>
                              <CTableDataCell>
                                <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                  {listing.listingID}
                                </span>
                              </CTableDataCell>
                              <CTableDataCell>
                                <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                  {listing.sellerID}
                                </span>
                              </CTableDataCell>
                              <CTableDataCell>{listing.price} USD</CTableDataCell>
                              <CTableDataCell>
                                <CBadge color={listing.status === 'LISTED' ? 'success' : 'warning'}>
                                  {listing.status}
                                </CBadge>
                              </CTableDataCell>
                              <CTableDataCell>
                                {listing.createdAt 
                                  ? new Date(listing.createdAt).toLocaleString()
                                  : 'N/A'}
                              </CTableDataCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    ) : (
                      <p className="mb-2">No listings found</p>
                    )}
                  </div>

                  {/* Sales Section */}
                  <div>
                    <h6>Sales ({nftHistory.sales?.length || 0})</h6>
                    {nftHistory.sales && nftHistory.sales.length > 0 ? (
                      <CTable hover responsive>
                        <CTableHead>
                          <CTableRow>
                            <CTableHeaderCell>Listing ID</CTableHeaderCell>
                            <CTableHeaderCell>Seller</CTableHeaderCell>
                            <CTableHeaderCell>Buyer</CTableHeaderCell>
                            <CTableHeaderCell>Price</CTableHeaderCell>
                            <CTableHeaderCell>Sold At</CTableHeaderCell>
                          </CTableRow>
                        </CTableHead>
                        <CTableBody>
                          {nftHistory.sales.map((sale, index) => (
                            <CTableRow key={sale.listingID || index}>
                              <CTableDataCell>
                                <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                  {sale.listingID}
                                </span>
                              </CTableDataCell>
                              <CTableDataCell>
                                <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                  {sale.sellerID}
                                </span>
                              </CTableDataCell>
                              <CTableDataCell>
                                <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                  {sale.buyerID}
                                </span>
                              </CTableDataCell>
                              <CTableDataCell>{sale.price} USD</CTableDataCell>
                              <CTableDataCell>
                                {sale.soldAt 
                                  ? new Date(sale.soldAt).toLocaleString()
                                  : 'N/A'}
                              </CTableDataCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    ) : (
                      <p className="mb-2">No sales found</p>
                    )}
                  </div>
                </div>
              )}

              {dataStatus.nftHistory === 'success' && !nftHistory && !errors.nftHistory && (
                <p className="mb-2">Enter an NFT ID and click Search to view history</p>
              )}
            </CCardBody>
          </CCard>

          {/* Listing History Card */}
          <CCard className="mb-4">
            <CCardHeader>
              <CIcon icon={cilHistory} className="me-2" />
              Listing History
            </CCardHeader>
            <CCardBody>
              <CInputGroup className="mb-3">
                <CFormInput
                  type="text"
                  placeholder="Enter Listing ID to view history"
                  value={listingId}
                  onChange={(e) => setListingId(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      fetchListingHistory();
                    }
                  }}
                />
                <CButton
                  color="primary"
                  onClick={fetchListingHistory}
                  disabled={dataStatus.listingHistory === 'loading' || !listingId}
                >
                  {dataStatus.listingHistory === 'loading' ? (
                    <>
                      <CSpinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    'Search'
                  )}
                </CButton>
              </CInputGroup>

              {errors.listingHistory && dataStatus.listingHistory === 'error' && (
                <div className="alert alert-danger mb-3">
                  <strong>Error:</strong> {errors.listingHistory}
                </div>
              )}

              {dataStatus.listingHistory === 'loading' && (
                <div className="text-center py-3">
                  <CSpinner />
                  <p className="mt-2">Loading listing history...</p>
                </div>
              )}

              {listingHistory.length > 0 && dataStatus.listingHistory === 'success' && (
                <CTable hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Status</CTableHeaderCell>
                      <CTableHeaderCell>Price</CTableHeaderCell>
                      <CTableHeaderCell>Seller</CTableHeaderCell>
                      <CTableHeaderCell>Buyer</CTableHeaderCell>
                      <CTableHeaderCell>Created At</CTableHeaderCell>
                      <CTableHeaderCell>Updated At</CTableHeaderCell>
                      <CTableHeaderCell>Sold At</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {listingHistory.map((listing, index) => (
                      <CTableRow key={listing.listingID || index}>
                        <CTableDataCell>
                          <CBadge color={
                            listing.status === 'LISTED' ? 'success' :
                            listing.status === 'SOLD' ? 'primary' :
                            listing.status === 'CANCELLED' ? 'danger' : 'warning'
                          }>
                            {listing.status}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell>{listing.price} USD</CTableDataCell>
                        <CTableDataCell>
                          <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                            {listing.sellerID}
                          </span>
                        </CTableDataCell>
                        <CTableDataCell>
                          {listing.buyerID ? (
                            <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                              {listing.buyerID}
                            </span>
                          ) : (
                            <span className="mb-2">N/A</span>
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {listing.createdAt 
                            ? new Date(listing.createdAt).toLocaleString()
                            : 'N/A'}
                        </CTableDataCell>
                        <CTableDataCell>
                          {listing.updatedAt 
                            ? new Date(listing.updatedAt).toLocaleString()
                            : 'N/A'}
                        </CTableDataCell>
                        <CTableDataCell>
                          {listing.soldAt 
                            ? new Date(listing.soldAt).toLocaleString()
                            : <span className="mb-2">N/A</span>}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              )}

              {dataStatus.listingHistory === 'success' && listingHistory.length === 0 && !errors.listingHistory && (
                <p className="mb-2">Enter a Listing ID and click Search to view history</p>
              )}
            </CCardBody>
          </CCard>
    </div>
    
  );
};

export default BlockchainExplorer;


